import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format } from "date-fns";
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Visibility,
    Download,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { formatNumber } from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';
import InventoryByProductIdDialog from './InventoryByProductIdDialog';
import ProductInventoryFilterDialog from './ProductInventoryFilterDialog';


const ProductInventoryByDate = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);

    const [openInventoryByProductIdDialog, setOpenInventoryByProductIdDialog] = useState(false);
    const [selectedProductName, setSelectedProductName] = useState(false);

    const [products, setProducts] = useState([]);
    const [productsInventory, setProductsInventory] = useState([]);
    const [inventoryByProductId, setInventoryByProductId] = useState([]);


    const fetchProducts = useCallback(() => {
        return api.get('/products');
    }, []);

    const fetchProductInventory = useCallback(() => {
        if (!startDate || !endDate) return [];

        const url = '/inflightsales/productinventorybydate';
        const payload = { startDate, endDate, timeMode };
        return api.post(url, payload);
    }, [timeMode, startDate, endDate]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                const [productsResponse, productInventoryResponse] =
                    await Promise.all([
                        fetchProducts(),
                        fetchProductInventory(),
                    ]);

                setProducts(productsResponse.data);
                setProductsInventory(productInventoryResponse.data);
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [fetchProducts, fetchProductInventory]);

    const filteredProductsInventory = useMemo(() => {
        if (!Array.isArray(productsInventory)) return [];
        if (!filters) return productsInventory;

        return productsInventory.filter((row) => {
            return Object.entries(filters).every(([key, value]) => {
                if (!value) return true;

                if (key === 'date') {
                    const formattedDate = row[key] ? format(new Date(row[key]), 'yyyy-MM-dd') : null;
                    const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                    return formattedDate === formattedFilterDate;
                }

                if (key === 'supplierName' || key === 'productName') {
                    return Array.isArray(value) ? value.includes(row[key]) : row[key] === value;
                }

                return row[key] === value;
            });
        });
    }, [productsInventory, filters]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);

    const handleFilterApply = (filterValues) => {
        setFilters(filterValues);
    };

    const supplierNameOptions = [...new Set(productsInventory?.map(product => product.supplierName))].sort();
    const productNameOptions = useMemo(() => {
        const productMap = new Map();
        products.forEach((p) => {
          if (p.productName && p.sortOrder !== undefined) {
            productMap.set(p.productName, p.sortOrder);
          }
        });

        const uniqueNames = [...new Set(productsInventory?.map(p => p.productName))];

        return uniqueNames.sort((a, b) => {
            const orderA = productMap.get(a);
            const orderB = productMap.get(b);
            if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
            if (orderA !== undefined) return -1;
            if (orderB !== undefined) return 1;
            return a.localeCompare(b);
        });

    }, [productsInventory, products]);


    const handleViewClick = useCallback(async (productId) => {
        try {
            const url = '/inflightsales/inventorybyproductid';
            const payload = { productId, startDate, endDate, timeMode };
            const res = await api.post(url, payload);

            setInventoryByProductId(res.data);
            setOpenInventoryByProductIdDialog(true);
        } catch (error) {
            toast.error(error || "Failed to fetch inventory by ProductId!");
        }
    }, [timeMode, startDate, endDate]);

    const handleDialogClose = () => setOpenInventoryByProductIdDialog(false);

    const handleExcelJSDownload = () => {
        if (!productsInventory || productsInventory.length === 0) {
            toast.error("No data available to export.");
            return;
        }
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14);
        const fileName = `ProductsInventory_${timestamp}.xlsx`;

        const filteredInventory = filteredProductsInventory?.map(product => {
            const { productId, ...rest } = product;
            return { ...rest, date: new Date(rest.date) };
        });

        exportToExcel(filteredInventory, fileName);
    };

    const columns = useMemo(() => {
        const commonNumericConfig = {
            headerClassName: 'theme-column--header',
            align: 'right',
            width: 135,
            valueFormatter: (params) => params ? Math.ceil(params).toLocaleString() : ''
        };

        return [
            {
                field: "no",
                headerName: "No",
                headerClassName: 'theme-column--header',
                filterable: false,
                sortable: false,
                width: 60,
                renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
            },
            {
                field: "date",
                headerName: "Date",
                headerClassName: 'theme-column--header',
                width: 100,
                valueFormatter: (value) => {
                    if (!value) return '';
                    return `${format(value, "dd/MM/yy")}`;
                },
            },
            {
                field: "supplierName",
                headerName: "SupplierName",
                headerClassName: 'theme-column--header',
                width: 150
            },
            {
                field: "productName",
                headerName: "ProductName",
                headerClassName: 'theme-column--header',
                width: 300
            },
            {
                field: "category",
                headerName: "Category",
                headerClassName: 'theme-column--header',
                width: 100
            },
            {
                field: "unit",
                headerName: "Unit",
                headerClassName: 'theme-column--header',
                width: 100
            },
            ...["preorder", "direct"].flatMap(prefix =>
                ["StockIn", "PreviousReceipt", "NextRetrieval", "StockOn", "Shrinkage", "Combo", "Loss", "Consume"]?.map(field => ({
                    field: `${prefix}${field}`,
                    headerName: `${prefix.charAt(0).toUpperCase() + prefix.slice(1)}${field}`,
                    ...commonNumericConfig
                }))
            )
        ];
    }, []);

    const actionColumn = useMemo(() => [{
        field: "action",
        headerName: "Action",
        width: 80,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={1}
                sx={{ width: "100%", height: "100%" }}
            >
                <Tooltip title="View">
                    <IconButton
                        aria-label="view"
                        sx={{
                            color: theme.palette.primary.dark,
                            "&:hover": { color: theme.palette.green.deep },
                        }}
                        onClick={() => {
                            setSelectedProductName(params?.row?.productName);
                            handleViewClick(params?.row?.productId);
                        }}
                    >
                        <Visibility />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
    }], [theme, handleViewClick]);


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Inventory by Date"
                    subtitle={`${loading ? 'Loading...' : 'List of Inventory Products'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton
                                    onClick={() => {
                                        handleExcelJSDownload();
                                        logClientAction("EXPORT_REPORT", { fileName: "ProductsInventoryByDate", format: "Excel", startDate, endDate });
                                    }}
                                >
                                    <Download />
                                </StyledButton>
                            </span>
                        </Tooltip>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12" }}
                    >
                        {isMobile ? (
                            <CardList
                                rows={filteredProductsInventory}
                                primaryField="productName"
                                secondaryField="supplierName"
                                minWidth={120}
                                fields={[
                                    { label: "Date", key: "date", render: (r) => r.date ? format(new Date(r.date), "dd/MM/yy") : null },
                                    { label: "Category", key: "category" },
                                    { label: "Unit", key: "unit" },
                                    { label: "Preorder StockIn",  key: "preorderStockIn",  cols: 2, render: (r) => formatNumber(r.preorderStockIn) },
                                    { label: "Direct StockIn",    key: "directStockIn",    cols: 2, render: (r) => formatNumber(r.directStockIn) },
                                    { label: "Preorder Consume",  key: "preorderConsume",  cols: 2, render: (r) => formatNumber(r.preorderConsume) },
                                    { label: "Direct Consume",    key: "directConsume",    cols: 2, render: (r) => formatNumber(r.directConsume) },
                                    { label: "Preorder StockOn",  key: "preorderStockOn",  cols: 2, render: (r) => formatNumber(r.preorderStockOn) },
                                    { label: "Direct StockOn",    key: "directStockOn",    cols: 2, render: (r) => formatNumber(r.directStockOn) },
                                ]}
                                isActiveField={null}
                                canView
                                canEdit={false}
                                canDelete={false}
                                onView={(id) => {
                                    const item = filteredProductsInventory.find(p => String(p._id) === String(id));
                                    if (item) {
                                        setSelectedProductName(item.productName);
                                        handleViewClick(item.productId);
                                    }
                                }}
                            />
                        ) : (
                            <DataGridComponent
                                rows={filteredProductsInventory}
                                columns={[...columns, ...actionColumn]}
                                loading={loading}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {isFilterDialogOpen &&
                <ProductInventoryFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    supplierNameOptions={supplierNameOptions}
                    productNameOptions={productNameOptions}
                    currentFilters={filters}
                />
            }

            {openInventoryByProductIdDialog &&
                <InventoryByProductIdDialog
                    open={openInventoryByProductIdDialog}
                    onClose={handleDialogClose}
                    selectedProductName={selectedProductName}
                    inventoryProducts={inventoryByProductId}
                />
            }
        </Box>
    );
};

export default ProductInventoryByDate;
